// src/services/setlistService.js
// Acceso a datos del setlist: repertorio (songs), setlist base de cada banda
// (setlist_templates) y setlist de cada evento (event_setlists).
//
// Ver migración supabase/migrations/20261019_setlists.sql. Las reglas de
// formato y las operaciones del editor están en src/domain/setlist.js.
//
// Concurrencia: al guardar un setlist que ya existía solo se acepta el cambio
// si su updated_at sigue siendo el que se leyó al abrirlo. Si otra persona ha
// guardado entretanto, no se pisa su versión: se lanza SetlistConflictError.

import { getSupabaseClient } from "./supabaseClient.js";
import { cleanText, contentStats, normalizeContent, prepareContentForSave, SETLIST_LIMITS } from "../domain/setlist.js";

const SONG_COLUMNS = "id, team_id, title, artist, song_key, capo_fret, notes, is_active, updated_at";
const SETLIST_COLUMNS = "id, event_id, header_names, content, created_at, updated_at";
const TEMPLATE_COLUMNS = "id, team_id, content, updated_at";

// Postgres: violación de un índice único.
const UNIQUE_VIOLATION = "23505";

export class SetlistConflictError extends Error {
  constructor(message) {
    super(message);
    this.name = "SetlistConflictError";
    this.code = "conflict";
  }
}

export class SongDuplicateError extends Error {
  constructor(message = "Esa canción ya está en el repertorio.") {
    super(message);
    this.name = "SongDuplicateError";
    this.code = "duplicate";
  }
}

// La petición no llega a Supabase (corte de red, cambio de wifi...): "Failed
// to fetch" en Chrome y Edge, "NetworkError" en Firefox, "Load failed" en Safari.
const NETWORK_ERROR = /failed to fetch|networkerror|load failed|network request failed/i;
const READ_RETRY_DELAYS_MS = [600, 1500];

export function isNetworkError(error) {
  return NETWORK_ERROR.test(String(error?.message ?? error ?? ""));
}

function fail(error, context) {
  const network = isNetworkError(error);
  const detail = network ? "no hay conexión con el servidor. Vuelve a intentarlo en unos segundos." : (error?.message || error);
  const e = new Error(`${context}: ${detail}`);
  e.code = network ? "network" : error?.code;
  e.raw = error;
  throw e;
}

// Lecturas: si la red falla un momento, se repite la consulta antes de dar
// error. Las escrituras no se repiten solas: podrían haberse guardado ya.
async function read(query) {
  let result = await query();
  for (const ms of READ_RETRY_DELAYS_MS) {
    if (!isNetworkError(result.error)) break;
    await new Promise(resolve => setTimeout(resolve, ms));
    result = await query();
  }
  return result;
}

// De la sesión guardada en el navegador: no hace falta preguntar al servidor.
async function currentUserId(supabase) {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id || null;
}

function toSetlist(row) {
  return row ? { ...row, content: normalizeContent(row.content) } : null;
}

// ─── Repertorio ─────────────────────────────────────────────────────────────

// Todo el repertorio de la banda, también las canciones desactivadas: un
// setlist antiguo puede seguir apuntando a ellas. El buscador ya las filtra.
export async function listTeamSongs(teamId) {
  if (!teamId) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await read(() => supabase
    .from("songs")
    .select(SONG_COLUMNS)
    .eq("team_id", teamId)
    .order("title", { ascending: true }));
  if (error) fail(error, "Error cargando el repertorio");
  return data || [];
}

// input: los datos limpios de validateSongInput(...).clean
export async function createSong(teamId, input) {
  if (!teamId) throw new Error("Falta la banda de la canción.");
  const supabase = getSupabaseClient();
  const userId = await currentUserId(supabase);
  const { data, error } = await supabase
    .from("songs")
    .insert({
      team_id: teamId,
      title: input.title,
      artist: input.artist,
      song_key: input.song_key || null,
      capo_fret: input.capo_fret ?? null,
      notes: input.notes || null,
      created_by: userId,
      updated_by: userId,
    })
    .select(SONG_COLUMNS)
    .single();
  if (error?.code === UNIQUE_VIOLATION) throw new SongDuplicateError();
  if (error) fail(error, "Error creando la canción");
  return data;
}

// Tono y cejilla habituales de una canción. Solo se usa cuando, desde un
// setlist, se marca expresamente "guardar también como tono habitual".
export async function updateSongDefaults(songId, changes = {}) {
  if (!songId) throw new Error("Falta la canción.");
  const supabase = getSupabaseClient();
  const patch = { updated_by: await currentUserId(supabase) };
  if ("song_key" in changes) patch.song_key = changes.song_key || null;
  if ("capo_fret" in changes) patch.capo_fret = changes.capo_fret ?? null;
  const { data, error } = await supabase
    .from("songs")
    .update(patch)
    .eq("id", songId)
    .select(SONG_COLUMNS)
    .single();
  if (error) fail(error, "Error guardando el tono habitual");
  return data;
}

// ─── Setlist de un evento ───────────────────────────────────────────────────

// Lo que el setlist necesita saber del evento: fecha y nombre para la
// cabecera, banda para el repertorio y ciudad del venue para el archivo.
export async function getEventForSetlist(eventId) {
  if (!eventId) throw new Error("Falta el evento.");
  const supabase = getSupabaseClient();
  const { data, error } = await read(() => supabase
    .from("events")
    .select("id, date, client_name_legacy, team_id, stage, city, region, event_venues(is_primary, venues:venue_id(city))")
    .eq("id", eventId)
    .single());
  if (error) fail(error, "Error cargando el evento");
  const links = data.event_venues || [];
  const primary = links.find(l => l.is_primary) || links[0] || null;
  const { event_venues: _links, ...event } = data;
  return { ...event, venueCity: primary?.venues?.city || null };
}

export async function getEventSetlist(eventId) {
  if (!eventId) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await read(() => supabase
    .from("event_setlists")
    .select(SETLIST_COLUMNS)
    .eq("event_id", eventId)
    .maybeSingle());
  if (error) fail(error, "Error cargando el setlist");
  return toSetlist(data);
}

// setlistId vacío = primer guardado (alta). Si no, expectedUpdatedAt es el
// updated_at leído al abrir el setlist.
export async function saveEventSetlist({ eventId, setlistId = null, headerNames = "", content, expectedUpdatedAt = null }) {
  if (!eventId) throw new Error("Falta el evento.");
  const supabase = getSupabaseClient();
  const userId = await currentUserId(supabase);
  const payload = {
    header_names: cleanText(headerNames).slice(0, SETLIST_LIMITS.headerNames),
    content: prepareContentForSave(content),
    updated_by: userId,
  };

  if (!setlistId) {
    const { data, error } = await supabase
      .from("event_setlists")
      .insert({ ...payload, event_id: eventId, created_by: userId })
      .select(SETLIST_COLUMNS)
      .single();
    if (error?.code === UNIQUE_VIOLATION) {
      throw new SetlistConflictError("Otra persona acaba de crear el setlist de este evento. Cierra y vuelve a abrirlo para ver su versión.");
    }
    if (error) fail(error, "Error guardando el setlist");
    return toSetlist(data);
  }

  if (!expectedUpdatedAt) throw new Error("Falta la versión del setlist que se está editando.");
  const { data, error } = await supabase
    .from("event_setlists")
    .update(payload)
    .eq("id", setlistId)
    .eq("updated_at", expectedUpdatedAt)
    .select(SETLIST_COLUMNS)
    .maybeSingle();
  if (error) fail(error, "Error guardando el setlist");
  if (!data) {
    throw new SetlistConflictError("Alguien ha modificado este setlist mientras lo editabas. Cierra y vuelve a abrirlo para ver su versión.");
  }
  return toSetlist(data);
}

// Resumen por evento para los semáforos de Producción, en una sola consulta.
// Devuelve un Map event_id -> { updatedAt, songs, lines }.
export async function listEventSetlistSummaries(eventIds) {
  const ids = [...new Set((eventIds || []).filter(Boolean))];
  if (!ids.length) return new Map();
  const supabase = getSupabaseClient();
  const { data, error } = await read(() => supabase
    .from("event_setlists")
    .select("event_id, updated_at, content")
    .in("event_id", ids));
  if (error) fail(error, "Error cargando los setlists");
  return new Map((data || []).map(row => {
    const stats = contentStats(normalizeContent(row.content));
    return [row.event_id, { updatedAt: row.updated_at, songs: stats.songs, lines: stats.lines }];
  }));
}

// ─── Setlist base de la banda ───────────────────────────────────────────────

export async function getSetlistTemplate(teamId) {
  if (!teamId) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await read(() => supabase
    .from("setlist_templates")
    .select(TEMPLATE_COLUMNS)
    .eq("team_id", teamId)
    .maybeSingle());
  if (error) fail(error, "Error cargando el setlist base");
  return toSetlist(data);
}

export async function saveSetlistTemplate({ teamId, templateId = null, content, expectedUpdatedAt = null }) {
  if (!teamId) throw new Error("Falta la banda.");
  const supabase = getSupabaseClient();
  const userId = await currentUserId(supabase);
  const payload = { content: prepareContentForSave(content), updated_by: userId };

  if (!templateId) {
    const { data, error } = await supabase
      .from("setlist_templates")
      .insert({ ...payload, team_id: teamId, created_by: userId })
      .select(TEMPLATE_COLUMNS)
      .single();
    if (error?.code === UNIQUE_VIOLATION) {
      throw new SetlistConflictError("Otra persona acaba de crear el setlist base. Cierra y vuelve a abrirlo para ver su versión.");
    }
    if (error) fail(error, "Error guardando el setlist base");
    return toSetlist(data);
  }

  if (!expectedUpdatedAt) throw new Error("Falta la versión del setlist base que se está editando.");
  const { data, error } = await supabase
    .from("setlist_templates")
    .update(payload)
    .eq("id", templateId)
    .eq("updated_at", expectedUpdatedAt)
    .select(TEMPLATE_COLUMNS)
    .maybeSingle();
  if (error) fail(error, "Error guardando el setlist base");
  if (!data) {
    throw new SetlistConflictError("Alguien ha modificado el setlist base mientras lo editabas. Cierra y vuelve a abrirlo para ver su versión.");
  }
  return toSetlist(data);
}
